import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ListAvailableCounterpartiesResponse } from "@upclaim/sdk/src/data-contracts";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { PaymentOrder, PaymentOrderStatus } from "../lib/payment-orders";

export type AvailableCounterparty = ListAvailableCounterpartiesResponse[number];

export interface PaymentOrderFilters {
  status: PaymentOrderStatus | null;
  claimNumber: string;
  /** No UI filter (parity with PaymentOrders.vue) but supported by the endpoint. */
  travelAgencyId: string;
  page: number;
  pageSize: number;
}

/** GET /admin/payments — mirrors PaymentOrders.vue fetchOrders. */
export function usePaymentOrders(filters: PaymentOrderFilters) {
  return useQuery({
    queryKey: queryKeys.payments.list({ ...filters }),
    queryFn: () =>
      adminAPI.listPaymentOrders({
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.claimNumber ? { claimNumber: Number(filters.claimNumber) } : {}),
        ...(filters.travelAgencyId ? { travelAgencyId: filters.travelAgencyId } : {}),
        page: filters.page,
        pageSize: filters.pageSize,
      }),
  });
}

/** Fires the actual SEPA transfer — always behind an explicit confirmation dialog. */
export function useConfirmPaymentOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (order: Pick<PaymentOrder, "id" | "claimId">) =>
      notify.promise(adminAPI.confirmPaymentOrder(order.claimId, order.id), {
        loading: "Confirmation du virement SEPA…",
        success: "Virement SEPA confirmé",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
    },
  });
}

export function useCancelPaymentOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (order: Pick<PaymentOrder, "id" | "claimId">) =>
      notify.promise(adminAPI.cancelPaymentOrder(order.claimId, order.id), {
        loading: "Annulation du virement SEPA…",
        success: "Virement SEPA annulé",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
    },
  });
}

/**
 * GET /admin/claims/by-number/{claimNumber}/available-counterparties — search step
 * of the create-order modal. Disabled until a claim number is submitted.
 */
export function useAvailableCounterparties(claimNumber: number | null) {
  return useQuery({
    queryKey: queryKeys.payments.availableCounterparties(claimNumber ?? 0),
    queryFn: () => adminAPI.listAvailableCounterparties(claimNumber as number),
    enabled: claimNumber != null,
    retry: false,
    staleTime: 0,
  });
}

export function useCreatePaymentOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { claimId: string; counterpartyId: string; amountInCents: number }) =>
      notify.promise(
        adminAPI.createPaymentOrder(payload.claimId, {
          counterpartyId: payload.counterpartyId,
          amountInCents: payload.amountInCents,
        }),
        {
          loading: "Création de l'ordre de paiement…",
          success: "Ordre de paiement créé",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
    },
  });
}
